import { forwardRef } from 'react';
import { ReadOnlyTooltip, useReadOnlyPause } from '../ReadOnly/ReadOnly';
import { SparkIcon } from '../SparkIcon';
import { resolveIconButtonTooltip } from '../shared/iconActionLabels';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import type { ButtonSize, ButtonVariant, IconButtonVariant, ButtonProps } from './Button.types';
import './Button.css';

export const ICON_BUTTON_VARIANTS: IconButtonVariant[] = [
  'primary',
  'secondary',
  'tertiary',
  'ghost',
];

/** Danger icon buttons: Primary, Secondary, Tertiary only (no Ghost danger in Figma). */
export const ICON_BUTTON_DANGER_VARIANTS: Exclude<IconButtonVariant, 'ghost'>[] = [
  'primary',
  'secondary',
  'tertiary',
];

/** Danger text buttons: Primary, Secondary, Ghost only (no Tertiary danger in Figma). */
export const TEXT_BUTTON_DANGER_VARIANTS: ButtonVariant[] = [
  'primary',
  'secondary',
  'ghost',
];

export function PlusIcon({ size }: { size: ButtonSize }) {
  if (size === 'tiny' || size === 'small') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function buttonSparkIconSize(size: ButtonSize): 'small' | 'medium' {
  return size === 'tiny' || size === 'small' ? 'small' : 'medium';
}

function resolveIconOnlyVariant(
  variant: ButtonVariant,
  danger: boolean,
): ButtonVariant {
  if (!ICON_BUTTON_VARIANTS.includes(variant as IconButtonVariant)) {
    return 'tertiary';
  }
  if (danger && variant === 'ghost') {
    return 'tertiary';
  }
  return variant;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  size = 'medium',
  variant = 'primary',
  danger = false,
  fill = false,
  inline = false,
  showStartIcon = false,
  showEndIcon = false,
  startIcon,
  icon,
  endIcon,
  children,
  className = '',
  disabled,
  type = 'button',
  tooltip,
  iconSlug,
  disableWhenReadOnly = false,
  'aria-label': ariaLabel,
  ...rest
}, ref) {
  const { paused: pausedByReadOnly, reason } = useReadOnlyPause(disableWhenReadOnly);
  const isDisabled = Boolean(disabled) || pausedByReadOnly;
  const explicitStartIcon = startIcon ?? icon;
  const resolvedStartIcon =
    explicitStartIcon ?? (showStartIcon ? <PlusIcon size={size} /> : undefined);
  const resolvedEndIcon =
    endIcon ?? (showEndIcon ? <SparkIcon slug="chevron-right" size={buttonSparkIconSize(size)} /> : undefined);
  const isIconOnly = !children;
  const resolvedVariant = isIconOnly ? resolveIconOnlyVariant(variant, danger) : variant;

  const classes = [
    'spark-btn',
    `spark-btn-${size}`,
    `spark-btn-${resolvedVariant}`,
    isIconOnly ? 'spark-btn-icon-only' : '',
    danger ? 'spark-btn-danger' : '',
    fill ? 'spark-btn-fill' : '',
    inline ? 'spark-btn-inline' : '',
    isDisabled ? 'spark-btn-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedAriaLabel = isIconOnly ? ariaLabel ?? 'Action' : ariaLabel;
  const anyIcon = resolvedStartIcon ?? resolvedEndIcon;

  const content = isIconOnly ? (
    <span className="spark-btn-icon">{anyIcon ?? <PlusIcon size={size} />}</span>
  ) : (
    <>
      {resolvedStartIcon ? (
        <span className="spark-btn-icon spark-btn-icon-start">{resolvedStartIcon}</span>
      ) : null}
      {children ? <span className="spark-btn-label">{children}</span> : null}
      {resolvedEndIcon ? <span className="spark-btn-icon spark-btn-icon-end">{resolvedEndIcon}</span> : null}
    </>
  );

  const button = (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-label={resolvedAriaLabel}
      {...rest}
    >
      {content}
    </button>
  );

  if (pausedByReadOnly && reason) {
    return (
      <ReadOnlyTooltip paused reason={reason}>
        {button}
      </ReadOnlyTooltip>
    );
  }

  if (isIconOnly) {
    const tooltipText = resolveIconButtonTooltip({
      tooltip,
      ariaLabel: resolvedAriaLabel,
      iconSlug,
    });
    return (
      <TooltipTrigger text={tooltipText} disabled={isDisabled}>
        {button}
      </TooltipTrigger>
    );
  }

  return button;
});

Button.displayName = 'Button';
